const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/hooks/useRemoteData.ts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Dispatch } from 'redux';
import { useSelector, useStore } from 'react-redux';
import { isEmpty } from '../utils/generalUtils';

/**
 * The minimum number of milliseconds that must pass between a fetches of any of two components using remoteData (or,
 * in fact, the same component). This value must never be 0, otherwise you risk that your component over-fetches or
 * even runs into an infinite loop. High values make loading data more sequential and can slow down overall loading
 * time. This value cannot be part of RemoteDataOptions, because it must be the same for all components.
 */
const MIN_MILLIS_BETWEEN_FETCHES = 100;

/**
 * Minimal number of milliseconds MAYBE_LOOP_RENDER_COUNT render cycles must happen within for useRemoteData to throw an
 * error.
 */
const MAYBE_LOOP_FETCH_MILLIS = 900;

/**
 * Minimal render count that must happen within MAYBE_LOOP_FETCH_MILLIS for useRemoteData to throw an error.
 */
const MAYBE_LOOP_RENDER_COUNT = 10;

/**
 * Data that will be passed to shouldFetch and fetch.
 */
export interface RemoteDataExtras<S> {
    selected: S;
    isLoading: boolean;
    hasJustBeenMounted: boolean;
    didFetch: boolean;
}

/**
 * Options per component that want to use useRemoteData.
 */
export interface RemoteDataOptions<P, S> {
    /**
     * Selector to determine whether data is currently loading
     */
    isLoading: (state: any, props: P) => boolean;

    /**
     * Selector to retrieve the desired data from the app state
     */
    select: (state: any, props: P) => S;

    /**
     * Determines whether the component should fetch data. It will not be called if the option noRefetch has already
     * prevented a refetch. If you implement this callback, make sure you include extras.isLoading in its body unless
     * the flag returned by isLoading is shared with other data that might be being fetched. If you do not implement
     * this callback, useRemoteData will use a default implementation.
     * @return true if data should be fetched, false otherwise
     */
    shouldFetch?: (state: any, props: P, extras: RemoteDataExtras<S>) => boolean;

    /**
     * Dispatches one or more actions to fetch data for this item
     */
    fetch: (dispatch: Dispatch<any>, state: any, props: P, extras: RemoteDataExtras<S>) => void;

    /**
     * Option to suppress fetches when the component has already called fetch once in the time it has been mounted.
     * Set this to true if you're sure you'll never need to look for newer data while the component stays mounted.
     */
    noRefetch?: boolean;

    /**
     * Optional string to be included in exceptions thrown by the hook.
     */
    errorContext?: string;
}

/**
 * This state information is shared by all components that use useRemoteData. We cannot use useRef for this, because we
 * need to protect overfetching across components (or across instances of the same component).
 */
const shared = {
    lastFetchMillis: 0,
};

/**
 * Default implementation of shouldFetch.
 */
function defaultShouldFetch<P, S>(_state: unknown, _props: P, extras: RemoteDataExtras<S>): boolean {
    return (extras.hasJustBeenMounted || isEmpty(extras.selected)) && !extras.isLoading;
}

export function useRemoteData<P, S = unknown>(
    props: P,
    getOptions: () => RemoteDataOptions<P, S>
): [S, boolean, () => void] {
    const staticData = useRef({
        renderCount: 0,
        didFetch: false,
        timerId: -1,
        lastFetchMillisMaybeLoop: 0,
        renderCountMaybeLoop: 0,
    });

    const options = useMemo(getOptions, []);
    const [_retryTime, setRetryTime] = useState(0);
    const store = useStore();
    const selected = useSelector(state => options.select(state, props));
    const isLoading = useSelector(state => options.isLoading(state, props));

    const extras: RemoteDataExtras<S> = {
        selected,
        isLoading,
        hasJustBeenMounted: staticData.current.renderCount === 0,
        didFetch: staticData.current.didFetch,
    };

    const shouldFetch = () => {
        if (options.noRefetch && staticData.current.didFetch) {
            return false; // re-fetching is suppressed
        }

        const fn = options.shouldFetch !== undefined ? options.shouldFetch : defaultShouldFetch;

        if (!fn(store.getState(), props, extras)) {
            return false; // shouldFetch decided we shouldn't fetch
        }

        if (Date.now() - shared.lastFetchMillis >= MIN_MILLIS_BETWEEN_FETCHES) {
            return true; // OK, fetch data
        }

        // The last fetch is too recent ago. Doing another fetch right now can lead to major inefficiencies or even
        // endless loops, because our isLoading may not have updated yet due to the asynchronous way how dispatch
        // works. We return false here, but we add a timer to remind us later.

        if (staticData.current.timerId >= 0) {
            window.clearTimeout(staticData.current.timerId);
        }

        const delay = MIN_MILLIS_BETWEEN_FETCHES - (Date.now() - shared.lastFetchMillis);

        staticData.current.timerId = window.setTimeout(() => {
            setRetryTime(Date.now()); // this will force a re-render, leading to a re-evaluation of shouldFetch
        }, delay);

        return false;
    };

    const checkForPotentialLoop = () => {
        const { lastFetchMillisMaybeLoop, renderCount } = staticData.current;

        if (!lastFetchMillisMaybeLoop) {
            // This is the first render since the last fetch.
            staticData.current.lastFetchMillisMaybeLoop = Date.now();
            staticData.current.renderCountMaybeLoop = 0;
        } else if (Date.now() - lastFetchMillisMaybeLoop > MAYBE_LOOP_FETCH_MILLIS) {
            // The last fetch is reasonably long ago, we can't count this as a loop.
            staticData.current.lastFetchMillisMaybeLoop = 0;
            staticData.current.renderCountMaybeLoop = 0;
        } else if (renderCount < MAYBE_LOOP_RENDER_COUNT) {
            // The last fetch is quite a short time ago, but the render count is still low. No loop yet...
            staticData.current.renderCountMaybeLoop++;
        } else {
            throw new Error(
                'useRemoteData: Detected endless loop! ' +
                    `Last fetch since ${Date.now() - lastFetchMillisMaybeLoop} ms, ` +
                    `render count is ${renderCount}, ` +
                    `errorContext=${options.errorContext}`
            );
        }
    };

    const fetch = (shouldCheckForPotentialLoop = false) => {
        options.fetch(store.dispatch, store.getState(), props, extras);
        staticData.current.didFetch = true;
        shared.lastFetchMillis = Date.now();

        if (shouldCheckForPotentialLoop) {
            checkForPotentialLoop();
        }
    };

    // We must not try to fetch right away, because this would indirectly lead to a setState within render phase.
    // Instead, we wrap it in a useEffect:
    useEffect(
        () => {
            if (shouldFetch()) {
                fetch(true);
            }

            staticData.current.renderCount++;
        },
        undefined // i.e. on each update
    );

    // Another useEffect to track the moment we get unmounted:
    useEffect(() => {
        return () => {
            if (staticData.current.timerId >= 0) {
                window.clearTimeout(staticData.current.timerId);
            }
        };
    }, []);

    return [extras.selected, extras.isLoading, () => fetch()];
}
